import {
    CardStyle,
    POSITION,
    DataType,
    ACTION_VARIANT,
} from "../../types/type";
import { IWidget } from "../../types/widgetTypes";
import { DOM } from "../../utils/domActions";
import Button from "../../components/Button";
import { TrackingUtil } from "../../utils/tracking";

class FloatingCard implements IWidget {
    cardItem: HTMLElement | null = null;
    cardData: Partial<DataType> = {};
    private actionHandler: (() => void) | null = null;
    private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;
    campaignId: string | null = null;
    cardStyle: CardStyle = {
        width: "254px",
        borderRadius: "16px",
        backgroundColor: "#fff",
        position: POSITION.LEFT_BOTTOM,
    };

    private isDismissed(): boolean {
        if (!this.campaignId) return false;

        const dismissedAt = localStorage.getItem(
            `trustshop_card_${this.campaignId}_dismissed`,
        );
        if (!dismissedAt) return false;

        const interval = this.cardData.reappear_interval;
        if (!interval) return true; // no reappear => permanently dismissed

        const elapsed = Date.now() - parseInt(dismissedAt, 10);
        if (interval === "after_1_day") return elapsed < 24 * 60 * 60 * 1000;
        if (interval === "after_7_days")
            return elapsed < 7 * 24 * 60 * 60 * 1000;

        return true; // unknown value => permanently dismissed
    }

    private markDismissed(): void {
        if (!this.campaignId) return;
        localStorage.setItem(
            `trustshop_card_${this.campaignId}_dismissed`,
            Date.now().toString(),
        );
    }

    /**
     * Handle widget positioning for floating card
     */
    handlePosition(container: HTMLElement, position?: string): void {
        if (!container || !this.cardItem) return;

        // Remove all position classes first
        container.classList.remove(
            "left-bottom",
            "left-top",
            "right-bottom",
            "right-top",
        );

        // Set transform origin based on position for smooth animations
        switch (position) {
            case "left-bottom":
            case POSITION.LEFT_BOTTOM:
                container.classList.add("left-bottom");
                this.cardItem.style.transformOrigin = "left bottom";
                break;
            case "left-top":
            case POSITION.LEFT_TOP:
                container.classList.add("left-top");
                this.cardItem.style.transformOrigin = "left top";
                break;
            case "right-bottom":
            case POSITION.RIGHT_BOTTOM:
                container.classList.add("right-bottom");
                this.cardItem.style.transformOrigin = "right bottom";
                break;
            case "right-top":
            case POSITION.RIGHT_TOP:
                container.classList.add("right-top");
                this.cardItem.style.transformOrigin = "right top";
                break;
            default:
                container.classList.add("right-bottom");
                this.cardItem.style.transformOrigin = "right bottom";
                break;
        }
    }

    render(): HTMLElement {
        if (this.cardItem) {
            return this.cardItem;
        }

        // Don't render if user has dismissed this card
        if (this.isDismissed()) {
            const emptyDiv = DOM.createElement("div", {
                styles: { display: "none" },
            });
            return emptyDiv;
        }

        if (window.TS_CARD_STYLE) {
            this.cardStyle = {
                ...this.cardStyle,
                ...window.TS_CARD_STYLE,
            };
        }

        this.cardItem = DOM.createElement("div", {
            id: "trustshop-embed-card--container",
            className:
                "trustshop-embed--card--container trustshop-embed--card-show",
            styles: this.cardStyle,
        });

        const cardWrap = DOM.createElement("div", {
            className: "trustshop-embed--card--wrap",
            styles: this.cardStyle,
        });

        if (this.cardData.dismissible !== false) {
            const closeBtn = Button.createClose({
                className: "trustshop-embed-card--close",
                onClick: () => {
                    this.markDismissed();
                    this.destroy();
                },
            });
            cardWrap.appendChild(closeBtn);
        }

        // Create banner safely
        const banner = DOM.createElement("div", {
            className: "trustshop-embed-card--banner",
        });

        if (this.cardData?.img_url) {
            const img = DOM.createElement("img", {
                attributes: {
                    src: this.cardData.img_url,
                    alt: "banner",
                },
            });
            banner.appendChild(img);
        }
        cardWrap.appendChild(banner);

        const cardContent = DOM.createElement("div", {
            className: "trustshop-embed-card--content",
        });

        // Create header section
        const contentHeader = DOM.createElement("div", {
            className: "trustshop-embed-card--content-header",
        });

        // Create title paragraph
        const titleParagraph = DOM.createElement("p", {
            className: "trustshop-embed-card--content-title",
        });

        // Add title text
        if (this.cardData?.title) {
            const titleSpan = DOM.createElement("span", {
                className: "trustshop-embed-card--content-title-text",
                textContent: this.cardData.title,
            });
            titleParagraph.insertAdjacentHTML(
                "beforeend",
                `${titleSpan.outerHTML} `,
            );
        }

        // Add badge if present
        if (this.cardData?.tag) {
            const badge = DOM.createElement("span", {
                className: `trustshop-embed-card--content-badge badge ${
                    this.cardData.tag.tone || ""
                }`,
                textContent: this.cardData.tag.label || "",
            });
            titleParagraph.appendChild(badge);
        }

        contentHeader.appendChild(titleParagraph);

        // Add date if present
        if (this.cardData?.date) {
            const dateSpan = DOM.createElement("span", {
                className: "trustshop-embed-card--content-date",
                textContent: this.cardData.date,
            });
            contentHeader.appendChild(dateSpan);
        }

        cardContent.appendChild(contentHeader);

        // Add description body if present
        if (this.cardData?.description) {
            const contentBody = DOM.createElement("div", {
                className: "trustshop-embed-card--content-body",
            });

            const descriptionParagraph = DOM.createElement("p", {
                className: "trustshop-embed-card--content-description",
                textContent: this.cardData.description,
            });

            contentBody.appendChild(descriptionParagraph);
            cardContent.appendChild(contentBody);
        }

        const secondaryActions =
            this.cardData.secondary_actions || this.cardData.secondary_action;
        if (this.cardData.primary_action || secondaryActions) {
            const cardFooter = DOM.createElement("div", {
                className: "trustshop-embed-card--content-footer",
            });

            if (this.cardData.primary_action) {
                // Primary action defaults to "primary" variant if not specified
                const variant =
                    this.cardData.primary_action.variant ||
                    ACTION_VARIANT.PRIMARY;

                const cardAction = Button.create({
                    ...this.cardData.primary_action,
                    variant: variant,
                    campaignId: this.campaignId || undefined,
                });

                cardFooter.appendChild(cardAction);
            }

            if (secondaryActions) {
                // Handle secondary actions (can be single or array)
                const secondaryList = Array.isArray(secondaryActions)
                    ? secondaryActions
                    : [secondaryActions];

                secondaryList.forEach((action) => {
                    const actionElement = Button.create({
                        ...action,
                        campaignId: this.campaignId || undefined,
                    });
                    cardFooter.appendChild(actionElement);
                });
            }
            cardContent.appendChild(cardFooter);
        }

        cardWrap.appendChild(cardContent);
        this.cardItem.appendChild(cardWrap);

        // Track view when card is successfully rendered
        TrackingUtil.trackView(this.campaignId);

        // Auto close after X seconds if configured
        if (
            this.cardData.auto_close_after &&
            this.cardData.auto_close_after > 0
        ) {
            this.autoCloseTimer = setTimeout(() => {
                this.destroy();
            }, this.cardData.auto_close_after * 1000);
        }

        return this.cardItem;
    }

    getCardItem(): HTMLElement | null {
        return this.cardItem;
    }

    action(callback: () => void): void {
        if (!this.cardItem) return;
        // Store the handler reference for cleanup
        this.actionHandler = callback;
        this.cardItem.addEventListener("click", this.actionHandler);
    }

    updateData(data: Partial<DataType>): void {
        this.cardData = {
            ...this.cardData,
            ...data,
        };

        // Get campaign ID from data
        if (data.id) {
            this.campaignId = String(data.id);
        }
    }

    show(): void {
        if (this.cardItem) {
            this.cardItem.style.display = "block";
        }
    }

    hide(): void {
        if (this.cardItem) {
            this.cardItem.style.display = "none";
        }
    }

    toggle(): void {
        if (this.isVisible()) {
            this.hide();
        } else {
            this.show();
        }
    }

    isVisible(): boolean {
        return this.cardItem ? this.cardItem.style.display !== "none" : false;
    }

    destroy(): void {
        if (!this.cardItem) return;

        if (this.autoCloseTimer) {
            clearTimeout(this.autoCloseTimer);
            this.autoCloseTimer = null;
        }

        // Remove action handler if it exists
        if (this.actionHandler) {
            this.cardItem.removeEventListener("click", this.actionHandler);
            this.actionHandler = null;
        }

        this.cardItem.remove();
        this.cardItem = null;
    }
}

const FloatingCardInstance = new FloatingCard();
export default FloatingCardInstance;
